import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BackHeader } from '../../components/BackHeader';
import { Building2, Loader, Trash2, Pencil, GitMerge, Phone, Mail, MapPin, CreditCard, StickyNote, UserPlus, CheckCircle, X, CalendarClock, Users, Plus, FileText, Percent } from 'lucide-react';
import { api } from '../../api/client';
import { toast } from 'sonner';
import { cleanFormPayload } from '../../lib/cleanFormPayload';
import { CONTACT_ROLE_OPTIONS } from '../../lib/contact-roles';
import ProvinceSelect from '../../components/ProvinceSelect';
import MergeCustomerDialog from '../../components/customers/MergeCustomerDialog';
import MergeContactDialog from '../../components/customers/MergeContactDialog';
import { possibleDuplicateIds } from '../../lib/contact-merge';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../../lib/load-failure';
import { updateQueryDataMapped } from '../../lib/queryClient';
import { canWriteCustomers, isCrmRole } from '../../lib/roles';
import { isSalesRole } from '../../lib/sales-access';
import BriefMeButton from '../../components/sales/BriefMeButton';
import { useAuth } from '../../contexts/AuthContext';
import { CustomerAccountStrip } from '../../components/customers/CustomerAccountStrip';
import { useCustomerPermissions } from '../../hooks/useCustomerPermissions';
import { AccountSnapshotPanel } from '../../components/customers/AccountSnapshotPanel';
import { CustomerMeetingsPanel, CustomerOpportunitiesPanel } from '../../components/customers/CustomerCrmPanels';

const PAYMENT_TERMS_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'Net 30', label: 'Net 30' },
  { value: 'Net 60', label: 'Net 60' },
  { value: 'Net 90', label: 'Net 90' },
  { value: 'Due on Receipt', label: 'Due on Receipt' },
];

const BILLING_FREQUENCY_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'per_event', label: 'Per Event' },
  { value: 'per_haul', label: 'Per Haul' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'per_well', label: 'Per Well' },
];

const DAY_OF_WEEK_OPTIONS = [
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
  { value: '7', label: 'Sunday' },
];

const BILLING_FREQUENCY_LABELS: Record<string, string> = {
  per_event: 'Per Event',
  per_haul: 'Per Haul',
  daily: 'Daily',
  weekly: 'Weekly',
  biweekly: 'Bi-weekly',
  monthly: 'Monthly',
  per_well: 'Per Well',
};

/**
 * A row of GET /customers/:customerId/contacts. Every text column is nullable —
 * Smith holds a contact with no name — and `rigName` null means customer-wide
 * ("All rigs"). `isPrimary` is gone: `is_primary` is deprecated, never written,
 * and was false on every TNT row, so the star it drew could only ever be absent.
 */
interface Contact {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  role: string | null;
  rigName: string | null;
}

const EMPTY_CONTACT = { name: '', phone: '', email: '', role: '', rigName: '' };
type ContactDraft = typeof EMPTY_CONTACT;

/**
 * Rig names are free text, and the API matches them LOWER(TRIM()). Grouping
 * uses the same idea plus collapsed inner runs, so "Ensign  144" and
 * "ensign 144" land in one group, displayed as the first spelling stored.
 */
const rigKey = (rig: string | null | undefined) =>
  (rig ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

export interface ContactGroup {
  key: string;
  /** null for the customer-wide group. */
  rigName: string | null;
  contacts: Contact[];
}

/** Rigs alphabetically, then "All rigs" (rigName null or blank) LAST. */
export function groupContactsByRig(contacts: Contact[]): ContactGroup[] {
  const groups = new Map<string, ContactGroup>();
  for (const c of contacts) {
    const key = rigKey(c.rigName);
    const g = groups.get(key);
    if (g) g.contacts.push(c);
    else groups.set(key, { key, rigName: key ? (c.rigName as string).trim() : null, contacts: [c] });
  }
  return Array.from(groups.values()).sort((a, b) => {
    if (a.rigName === null) return 1;
    if (b.rigName === null) return -1;
    return a.rigName.localeCompare(b.rigName, undefined, { sensitivity: 'base', numeric: true });
  });
}

function billingAnchorLabel(frequency: string, anchor: number | string | null): string {
  if (!anchor) return '';
  const n = Number(anchor);
  if (frequency === 'weekly' || frequency === 'biweekly') {
    const day = DAY_OF_WEEK_OPTIONS.find((d) => d.value === String(n));
    return day ? day.label : String(n);
  }
  if (frequency === 'monthly') return `Day ${n}`;
  return String(n);
}

const inputClass =
  'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-amber-500/50 transition-colors';

export const CustomerDetail: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  // The API's CUSTOMER_WRITE_ROLES. A field role reads contacts and is offered
  // no control it would be refused on.
  const canWriteContacts = canWriteCustomers(user?.role);
  // Sales reads clients and contacts and writes nothing (operator, 2026-10-11;
  // api#1032). The header's Invite / Edit / Delete are not drawn for them, and
  // the rate-sheet list (refused to sales) is not fetched — the client's own
  // rate-sheet name then renders as absent rather than as a refusal.
  const isSales = isSalesRole(user?.role);
  // Sales may edit a PROSPECT's fields (operator, 2026-10-12; api#1038 answers
  // 403 sales_prospect_only on any other customer). Invite and delete stay
  // office-only.
  const customerPerms = useCustomerPermissions();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [mergeDialog, setMergeDialog] = useState<{
    open: boolean;
    liveReferenceCount: number;
    countsPerTable: Record<string, number>;
  }>({ open: false, liveReferenceCount: 0, countsPerTable: {} });
  const [editing, setEditing] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteStatus, setInviteStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [inviteError, setInviteError] = useState('');

  // Edit form state
  const [editName, setEditName] = useState('');
  const [editContactName, setEditContactName] = useState('');
  const [editContactPhone, setEditContactPhone] = useState('');
  const [editContactEmail, setEditContactEmail] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [editCity, setEditCity] = useState('');
  const [editProvince, setEditProvince] = useState('');
  const [editPaymentTerms, setEditPaymentTerms] = useState('');
  const [editAccountingEmail, setEditAccountingEmail] = useState('');
  const [editBillingFrequency, setEditBillingFrequency] = useState('');
  const [editBillingAnchor, setEditBillingAnchor] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editDefaultRateSheetId, setEditDefaultRateSheetId] = useState('');
  const [editPstExempt, setEditPstExempt] = useState(false);
  const [editPstExemptNumber, setEditPstExemptNumber] = useState('');

  // Query
  const { data: customer, isLoading, isError } = useQuery({
    queryKey: ['customer', customerId],
    queryFn: () => api.customers.get(customerId!).then((r) => r.data.data),
    enabled: !!customerId,
  });

  // Rate sheets for the default picker. Only the COMPANY-WIDE books are
  // offered: a default exists to break a tie between books for a client who
  // has no contract of their own, and a client's own contract already wins on
  // its own tier without any default being set.
  const { data: rateSheets = [] } = useQuery({
    queryKey: ['rate-sheets'],
    enabled: !isSales,
    queryFn: () => api.rateSheets.list().then((r) => {
      const body = r.data;
      const rows = 'data' in body ? body.data : body;
      return Array.isArray(rows) ? rows : [];
    }),
  });
  const companyBooks = (rateSheets as any[]).filter(
    (rs) => (rs.customerId ?? rs.customer_id ?? null) === null
  );

  // Populate edit form
  function populateForm() {
    if (!customer) return;
    setEditName(customer.name || '');
    setEditContactName(customer.mainContactName || customer.main_contact_name || '');
    setEditContactPhone(customer.mainContactPhone || customer.main_contact_phone || '');
    setEditContactEmail(customer.mainContactEmail || customer.main_contact_email || '');
    setEditAddress(customer.address || '');
    setEditCity(customer.city || '');
    setEditProvince(customer.provinceState || customer.province_state || '');
    setEditPaymentTerms(customer.paymentTerms || customer.payment_terms || '');
    setEditAccountingEmail(customer.accountingEmail || customer.accounting_email || '');
    setEditBillingFrequency(customer.defaultBillingFrequency || customer.default_billing_frequency || '');
    // `billingAnchorDay` — the ONE name, across this read, the create modal and
    // the PUT. It is the Drizzle property for `customers.billing_anchor_day`, so
    // it is what GET /customers/:id actually returns; `billingAnchor` was read
    // here and returned by nothing, so this field loaded blank on every
    // customer and Save sent null for a PUT alias to write onto a live column
    // (fe#660, api#708). No snake_case sibling: the recorded read for this
    // endpoint is camelCase only, and a second spelling here is what produced
    // the bug.
    setEditBillingAnchor(customer.billingAnchorDay != null ? String(customer.billingAnchorDay) : '');
    setEditNotes(customer.notes || '');
    setEditDefaultRateSheetId(customer.defaultRateSheetId || customer.default_rate_sheet_id || '');
    // Reads tolerate both casings — the API answers camelCase and the raw pg
    // shape reaches some callers. `=== true` rather than a truthiness test: a
    // boolean column is the one place a string 'false' would read as exempt.
    setEditPstExempt((customer.pstExempt ?? customer.pst_exempt) === true);
    setEditPstExemptNumber(customer.pstExemptNumber || customer.pst_exempt_number || '');
  }

  // Mutations
  const deleteMutation = useMutation({
    mutationFn: () => api.customers.delete(customerId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      navigate('/customers', { replace: true });
    },
    onError: (err: any) => {
      // 409 = live references block the delete. Offer a merge escape hatch instead
      // of surfacing the raw error.
      if (err?.response?.status === 409) {
        const data = err.response.data?.data;
        setShowDeleteConfirm(false);
        setMergeDialog({
          open: true,
          liveReferenceCount: data?.total_live ?? 0,
          countsPerTable: data?.counts_per_table ?? {},
        });
      } else {
        toast.error(err?.response?.data?.error || 'Failed to delete customer');
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: any) => api.customers.update(customerId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', customerId] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setEditing(false);
    },
    // There was no onError at all. The save now has a way to be REFUSED —
    // default_rate_sheet_id is validated against the tenant — and without this
    // a 400 would close nothing, say nothing, and leave the form looking as
    // though it had simply not responded.
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Failed to save client');
    },
  });

  // ─── Contacts ────────────────────────────────────────────────
  // `error` is destructured on purpose: a failed load is not "No contacts yet".
  const { data: contacts = [], isLoading: contactsLoading, error: contactsError } = useQuery<Contact[]>({
    queryKey: ['customer-contacts', customerId],
    queryFn: () => api.customers.listContacts(customerId!).then((r) => {
      const d = r.data;
      return Array.isArray(d) ? d : (d?.data ?? d?.contacts ?? []);
    }),
    enabled: !!customerId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

  const [showAddContact, setShowAddContact] = useState(false);
  const [newContact, setNewContact] = useState(EMPTY_CONTACT);
  const [editingContactId, setEditingContactId] = useState<string | null>(null);
  const [editContactState, setEditContactState] = useState(EMPTY_CONTACT);
  const [contactToDelete, setContactToDelete] = useState<Contact | null>(null);
  const [contactToMerge, setContactToMerge] = useState<Contact | null>(null);
  const [mergeError, setMergeError] = useState<string | null>(null);

  // Rig suggestions for the contact form: every rig this tenant has run a job
  // on, plus the rigs already on this client's contacts. Fetched only while a
  // form is open. A failed fetch leaves the field as free text, which it is
  // anyway — suggestions are a convenience, not a vocabulary.
  const contactFormOpen = showAddContact || editingContactId !== null;
  const { data: jobRigs = [] } = useQuery<string[]>({
    queryKey: ['job-rigs'],
    queryFn: () => api.jobs.listRigs().then((r) => {
      const body = r.data;
      const rows = body && typeof body === 'object' && 'data' in body ? body.data : body;
      return Array.isArray(rows) ? rows : [];
    }),
    enabled: contactFormOpen,
  });
  const rigSuggestions = (() => {
    const seen = new Map<string, string>();
    for (const rig of [...contacts.map((c) => c.rigName), ...jobRigs]) {
      const k = rigKey(rig);
      if (k && !seen.has(k)) seen.set(k, (rig as string).trim());
    }
    return Array.from(seen.values()).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true }));
  })();

  // ADD omits a blank field — the key is absent, not `undefined` — because
  // there is nothing stored for it to clear. A blank rig is customer-wide.
  const createContactMutation = useMutation({
    mutationFn: (data: ContactDraft) => {
      const body: Record<string, string> = { name: data.name.trim() };
      const phone = data.phone.trim();
      const email = data.email.trim();
      const rigName = data.rigName.trim();
      if (phone) body.phone = phone;
      if (email) body.email = email;
      if (data.role) body.role = data.role;
      if (rigName) body.rigName = rigName;
      return api.customers.createContact(customerId!, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-contacts', customerId] });
      setShowAddContact(false);
      setNewContact(EMPTY_CONTACT);
      toast.success('Contact added');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to add contact');
    },
  });

  // EDIT sends a cleared field as `null`, never `undefined`. PATCH writes only
  // the keys it is handed, so `|| undefined` DROPPED a cleared phone from the
  // body and the old number stayed — the edit looked saved and changed nothing.
  // `null` clears it (api#844). A blank rig makes the contact customer-wide.
  const updateContactMutation = useMutation({
    mutationFn: ({ contactId, data }: { contactId: string; data: ContactDraft }) =>
      api.customers.updateContact(customerId!, contactId, {
        name: data.name.trim(),
        phone: data.phone.trim() || null,
        email: data.email.trim() || null,
        role: data.role || null,
        rigName: data.rigName.trim() || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-contacts', customerId] });
      setEditingContactId(null);
      toast.success('Contact updated');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to update contact');
    },
  });

  const deleteContactMutation = useMutation({
    mutationFn: (contactId: string) => api.customers.deleteContact(customerId!, contactId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-contacts', customerId] });
      setContactToDelete(null);
      toast.success('Contact removed');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to remove contact');
    },
  });

  // The URL carries KEEP; the contact the action was opened on is FROM and is
  // retired. On success the card shows the row the SERVER returned — written
  // into the cache before the refetch — never the dialog's preview.
  const mergeContactMutation = useMutation({
    mutationFn: ({ keepId, fromId }: { keepId: string; fromId: string }) =>
      api.customers.mergeContacts(customerId!, keepId, fromId).then((r): Contact | undefined => r.data?.data),
    onSuccess: (kept, { fromId }) => {
      if (kept?.id) {
        updateQueryDataMapped<Contact[]>(queryClient, ['customer-contacts', customerId], (prev) =>
          prev?.filter((c) => c.id !== fromId).map((c) => (c.id === kept.id ? kept : c)));
      }
      queryClient.invalidateQueries({ queryKey: ['customer-contacts', customerId] });
      setContactToMerge(null);
      toast.success(`Merged into ${kept?.name?.trim() || 'the kept contact'}`);
    },
    onError: (err: any) => {
      // Either id gone (another tab merged or removed it): the list is stale.
      if (err?.response?.status === 404) {
        queryClient.invalidateQueries({ queryKey: ['customer-contacts', customerId] });
        setContactToMerge(null);
        toast.error('That contact was already removed — refreshed.');
        return;
      }
      setMergeError(err?.response?.data?.error || 'Failed to merge contacts');
    },
  });

  function startEditContact(c: Contact) {
    setEditingContactId(c.id);
    setEditContactState({ name: c.name || '', phone: c.phone || '', email: c.email || '', role: c.role || '', rigName: c.rigName || '' });
  }

  // Auto-enter edit mode from ?edit=true
  useEffect(() => {
    if (searchParams.get('edit') === 'true' && customer && !editing) {
      populateForm();
      setEditing(true);
      searchParams.delete('edit');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, searchParams]);

  function startEdit() {
    populateForm();
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
  }

  async function handleInvite() {
    if (!inviteEmail.trim()) return;
    setInviteStatus('sending');
    try {
      await api.customers.inviteToPortal(customerId!, { email: inviteEmail.trim() });
      setInviteStatus('sent');
      setTimeout(() => {
        setShowInviteModal(false);
        setInviteStatus('idle');
        setInviteEmail('');
      }, 2000);
    } catch (err: any) {
      setInviteError(err.response?.data?.error || 'Failed to send invite');
      setInviteStatus('error');
    }
  }

  function openInviteModal() {
    const email = contactEmail || customer?.accountingEmail || customer?.accounting_email || '';
    setInviteEmail(email);
    setInviteStatus('idle');
    setInviteError('');
    setShowInviteModal(true);
  }

  function handleSave() {
    if (!editName.trim()) return;
    updateMutation.mutate(cleanFormPayload({
      name: editName.trim(),
      mainContactName: editContactName.trim() || null,
      mainContactPhone: editContactPhone.trim() || null,
      mainContactEmail: editContactEmail.trim() || null,
      accountingEmail: editAccountingEmail.trim() || null,
      address: editAddress.trim() || null,
      city: editCity.trim() || null,
      provinceState: editProvince.trim() || null,
      paymentTerms: editPaymentTerms || null,
      defaultBillingFrequency: editBillingFrequency || null,
      billingAnchorDay: editBillingAnchor,
      notes: editNotes.trim() || null,
      // '' → null clears the default, which must stay expressible.
      defaultRateSheetId: editDefaultRateSheetId || null,
      // camelCase, because `PUT /customers/:id` sanitises against the Drizzle
      // COLUMN NAMES — the snake_case spelling is dropped silently, which is
      // the same trap the equipment cert fields carry. A real boolean, not a
      // string: the route refuses anything else rather than letting Postgres
      // decide, so 'false' cannot arrive and read as exempt.
      pstExempt: editPstExempt,
      pstExemptNumber: editPstExemptNumber.trim() || null,
    }, {
      numericFields: ['billingAnchorDay'],
    }));
  }

  // Read-only computed values
  const pstExempt = (customer?.pstExempt ?? customer?.pst_exempt) === true;
  const pstExemptNumber = customer?.pstExemptNumber || customer?.pst_exempt_number || '';
  const contactName = customer?.mainContactName || customer?.main_contact_name;
  const contactPhone = customer?.mainContactPhone || customer?.main_contact_phone;
  const contactEmail = customer?.mainContactEmail || customer?.main_contact_email;
  const accountingEmail = customer?.accountingEmail || customer?.accounting_email;
  // Resolved to a NAME rather than shown as an id, and absent when the sheet is
  // gone — a stale id rendered raw would read as configuration when it is rot.
  const defaultRateSheetName = (() => {
    const id = customer?.defaultRateSheetId ?? customer?.default_rate_sheet_id;
    if (!id) return null;
    return (rateSheets as any[]).find((rs) => rs.id === id)?.name ?? null;
  })();
  const city = customer?.city;
  const province = customer?.provinceState || customer?.province_state;
  const address = customer?.address;
  const paymentTerms = customer?.paymentTerms || customer?.payment_terms;
  const defaultBillingFrequency = customer?.defaultBillingFrequency || customer?.default_billing_frequency;
  const billingAnchor = customer?.billingAnchorDay ?? null;
  const notes = customer?.notes;

  // Shared contacts card props
  const contactsCardProps = {
    contacts,
    contactsLoading,
    contactsError,
    canWrite: canWriteContacts,
    customerName: customer?.name ?? '',
    rigSuggestions,
    showAddContact,
    setShowAddContact,
    newContact,
    setNewContact,
    editingContactId,
    editContactState,
    setEditContactState,
    onAdd: () => createContactMutation.mutate(newContact),
    addPending: createContactMutation.isPending,
    onSaveEdit: (contactId: string) => updateContactMutation.mutate({ contactId, data: editContactState }),
    editPending: updateContactMutation.isPending,
    onCancelEdit: () => setEditingContactId(null),
    onStartEdit: startEditContact,
    onDelete: (c: Contact) => setContactToDelete(c),
    onMerge: (c: Contact) => { setMergeError(null); setContactToMerge(c); },
  };

  // One confirm for both modes of the page. Past jobs and tickets carry their
  // own copy of the contact, and the delete is soft (api#844), so the sentence
  // promises nothing the server does not do.
  const contactDeleteConfirm = (
    <ConfirmModal
      isOpen={contactToDelete !== null}
      title="Remove contact"
      body={`Remove ${contactToDelete?.name?.trim() || 'this unnamed contact'} from ${customer?.name || 'this client'}? Past jobs and tickets keep their copy.`}
      confirmLabel="Remove"
      loading={deleteContactMutation.isPending}
      onConfirm={() => contactToDelete && deleteContactMutation.mutate(contactToDelete.id)}
      onCancel={() => setContactToDelete(null)}
    />
  );

  // Only this client's OTHER live contacts — the list endpoint returns live
  // rows only, and the source is never a candidate to merge into itself.
  const contactMergeDialog = contactToMerge && (
    <MergeContactDialog
      from={contactToMerge}
      candidates={groupContactsByRig(contacts.filter((c) => c.id !== contactToMerge.id))}
      onConfirm={(keepId) => { setMergeError(null); mergeContactMutation.mutate({ keepId, fromId: contactToMerge.id }); }}
      onCancel={() => setContactToMerge(null)}
      pending={mergeContactMutation.isPending}
      error={mergeError}
    />
  );

  if (isLoading) {
    return (
      <div className="min-h-full bg-fieldiq-dark flex items-center justify-center">
        <Loader size={32} className="animate-spin text-fieldiq-accent" />
      </div>
    );
  }

  if (isError || !customer) {
    return (
      <div className="min-h-full bg-fieldiq-dark">
        <BackHeader to="/customers" label="Clients" />
        <div className="text-center py-16">
          <Building2 size={48} className="mx-auto text-white mb-4" />
          <p className="text-white text-lg">Client not found</p>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // EDIT MODE
  // ═══════════════════════════════════════════════════════════════
  if (editing) {
    return (
      <div className="animate-fade-in min-h-full pb-[var(--bottom-chrome)] bg-fieldiq-dark">
        <BackHeader
          to="/customers"
          label="Clients"
          title="Editing Client"
          rightAction={
            <>
              <button
                onClick={cancelEdit}
                disabled={updateMutation.isPending}
                className="px-4 py-2 bg-fieldiq-card border border-fieldiq-border text-white font-semibold rounded-lg text-sm hover:bg-fieldiq-card/80 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={updateMutation.isPending || !editName.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-bold rounded-lg text-sm hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {updateMutation.isPending && <Loader size={14} className="animate-spin" />}
                {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          }
        />

        <main className="max-w-[1920px] mx-auto px-4 py-4 space-y-4">
          <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-4 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Building2 size={16} className="text-fieldiq-accent" />
              <h3 className="text-xs font-bold text-white tracking-wider uppercase">Details</h3>
            </div>

            {updateMutation.isError && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg p-3 text-sm">
                {(updateMutation.error as any)?.response?.data?.error || 'Failed to save changes'}
              </div>
            )}

            <div>
              <label className="text-xs text-white block mb-1">Company Name *</label>
              <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} className={inputClass} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-white block mb-1">Main Contact Name</label>
                <input type="text" value={editContactName} onChange={(e) => setEditContactName(e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className="text-xs text-white block mb-1">Phone</label>
                <input type="tel" value={editContactPhone} onChange={(e) => setEditContactPhone(e.target.value)} className={inputClass} />
              </div>
            </div>

            <div>
              <label className="text-xs text-white block mb-1">Email</label>
              <input type="email" value={editContactEmail} onChange={(e) => setEditContactEmail(e.target.value)} className={inputClass} />
            </div>

            <div>
              <label className="text-xs text-white block mb-1">Accounting / AR Email</label>
              <input type="email" value={editAccountingEmail} onChange={(e) => setEditAccountingEmail(e.target.value)} className={inputClass} placeholder="ar@company.com" />
            </div>

            <div>
              <label className="text-xs text-white block mb-1">Address</label>
              <input type="text" value={editAddress} onChange={(e) => setEditAddress(e.target.value)} className={inputClass} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-white block mb-1">City</label>
                <input type="text" value={editCity} onChange={(e) => setEditCity(e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className="text-xs text-white block mb-1">Province / State</label>
                <ProvinceSelect value={editProvince} onChange={setEditProvince} className={inputClass} />
              </div>
            </div>

            <div>
              <label className="text-xs text-white block mb-1">Payment Terms</label>
              <select value={editPaymentTerms} onChange={(e) => setEditPaymentTerms(e.target.value)} className={inputClass}>
                {PAYMENT_TERMS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* ── Tax ────────────────────────────────────────────────────
                PST exempt is NOT the same thing as tax exempt, and the
                difference is money. An oilfield client in BC is routinely
                exempt from PST while remaining fully liable for GST; the
                existing `tax_exempt` flag is all-or-nothing and recording that
                client with it would drop the GST too.

                Per CLIENT, deliberately. A tenant-level switch would be wrong
                for every non-exempt client they also invoice, and would have to
                be remembered and un-set per invoice.
            */}
            <div className="border-t border-fieldiq-border pt-4 mt-2">
              <div className="flex items-center gap-2 mb-3">
                <Percent size={16} className="text-fieldiq-accent" />
                <h3 className="text-xs font-bold text-white tracking-wider uppercase">Tax</h3>
              </div>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editPstExempt}
                  onChange={(e) => setEditPstExempt(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-fieldiq-accent"
                />
                <span>
                  <span className="text-sm text-white block">PST exempt</span>
                  <span className="text-xs text-fieldiq-muted block">
                    No PST on this client's invoices. GST is charged as normal.
                    Only applies where the province levies a separate PST.
                  </span>
                </span>
              </label>

              {editPstExempt && (
                <div className="mt-3">
                  <label className="text-xs text-white block mb-1" htmlFor="pst-exempt-number">
                    PST exemption number
                  </label>
                  <input
                    id="pst-exempt-number"
                    type="text"
                    value={editPstExemptNumber}
                    onChange={(e) => setEditPstExemptNumber(e.target.value)}
                    placeholder="Certificate number (optional)"
                    className={inputClass}
                  />
                  {/*
                    Optional on purpose. Blocking the exemption on a number the
                    operator does not have to hand produces a placeholder, and a
                    placeholder certificate number is worse data than a blank
                    one — it reads as verified.
                  */}
                </div>
              )}
            </div>

            {/* Billing Section */}
            <div className="border-t border-fieldiq-border pt-4 mt-2">
              <div className="flex items-center gap-2 mb-3">
                <CalendarClock size={16} className="text-fieldiq-accent" />
                <h3 className="text-xs font-bold text-white tracking-wider uppercase">Billing</h3>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-xs text-white block mb-1">Default Billing Frequency</label>
                  <select
                    value={editBillingFrequency}
                    onChange={(e) => { setEditBillingFrequency(e.target.value); setEditBillingAnchor(''); }}
                    className={inputClass}
                  >
                    {BILLING_FREQUENCY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Only worth showing where there is a tie to break. A tenant with
                    one book (or none) has no decision here, and rendering a
                    one-option select would imply otherwise. */}
                {companyBooks.length > 1 && (
                  <div>
                    <label className="text-xs text-white block mb-1">Default Price Book</label>
                    <select
                      value={editDefaultRateSheetId}
                      onChange={(e) => setEditDefaultRateSheetId(e.target.value)}
                      className={inputClass}
                    >
                      <option value="">No default — ask each time</option>
                      {companyBooks.map((rs: any) => (
                        <option key={rs.id} value={rs.id}>{rs.name}</option>
                      ))}
                    </select>
                    <p className="text-xs text-fieldiq-muted mt-1">
                      Used when this client has no rate sheet of their own and more than one
                      company price book could apply. A job can still override it.
                    </p>
                  </div>
                )}

                {(editBillingFrequency === 'weekly' || editBillingFrequency === 'biweekly') && (
                  <div>
                    <label className="text-xs text-white block mb-1">Billing Anchor (Day of Week)</label>
                    <select
                      value={editBillingAnchor}
                      onChange={(e) => setEditBillingAnchor(e.target.value)}
                      className={inputClass}
                    >
                      <option value="">Select day...</option>
                      {DAY_OF_WEEK_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <p className="text-xs text-fieldiq-muted mt-1">Invoices will be generated on this day of the week</p>
                  </div>
                )}

                {editBillingFrequency === 'monthly' && (
                  <div>
                    <label className="text-xs text-white block mb-1">Billing Anchor (Day of Month)</label>
                    <input
                      type="number"
                      min={1}
                      max={31}
                      value={editBillingAnchor}
                      onChange={(e) => setEditBillingAnchor(e.target.value)}
                      placeholder="1-31"
                      className={inputClass}
                    />
                    <p className="text-xs text-fieldiq-muted mt-1">Invoices will be generated on this day of the month</p>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="text-xs text-white block mb-1">Notes</label>
              <textarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)} rows={3} className={inputClass} />
            </div>
          </div>

          {/* Contacts Card */}
          <ContactsCard {...contactsCardProps} />
          {contactDeleteConfirm}
          {contactMergeDialog}
        </main>

      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // READ MODE
  // ═══════════════════════════════════════════════════════════════
  return (
    <div className="animate-fade-in min-h-full pb-[var(--bottom-chrome)] bg-fieldiq-dark">
      <BackHeader
        to="/customers"
        label="Clients"
        title={customer.name}
        rightAction={
          isSales ? (
            <>
              <BriefMeButton customerName={customer.name} />
              {customerPerms.canEdit(customer) && (
                <button
                  onClick={startEdit}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-bold rounded-lg text-sm hover:bg-primary/90 transition-colors"
                >
                  <Pencil size={14} />
                  Edit
                </button>
              )}
            </>
          ) : <>
            <BriefMeButton customerName={customer.name} />
            <button
              onClick={openInviteModal}
              className="flex items-center gap-2 px-3 py-2 bg-fieldiq-card border border-fieldiq-border text-white font-semibold rounded-lg text-sm hover:bg-fieldiq-card/80 transition-colors"
            >
              <UserPlus size={14} />
              Invite to Portal
            </button>
            <button
              onClick={startEdit}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-bold rounded-lg text-sm hover:bg-primary/90 transition-colors"
            >
              <Pencil size={14} />
              Edit
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center justify-center w-9 h-9 bg-fieldiq-card border border-fieldiq-border rounded-lg text-red-400 hover:bg-red-500/10 transition-colors"
            >
              <Trash2 size={14} />
            </button>
          </>
        }
      />

      <main className="max-w-[1920px] mx-auto px-4 py-4 space-y-4">
        <CustomerAccountStrip customer={{ ...customer, id: customerId! }} />

        {/* The account view (CRM-FE-4). CRM_ROLES only: the snapshot,
            meetings and opportunities are all CRM_ROLES routes, so any other
            role would see three refusals. The notes blob below is unchanged. */}
        {isCrmRole(user?.role) && (
          <>
            <AccountSnapshotPanel customerId={customerId!} />
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <CustomerMeetingsPanel customerId={customerId!} />
              <CustomerOpportunitiesPanel customer={{ id: customerId!, name: customer.name, status: customer.status }} />
            </div>
          </>
        )}

        {/* Details Card */}
        <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-4">
          <div className="flex items-center gap-2 mb-4">
            <Building2 size={16} className="text-fieldiq-accent" />
            <h3 className="text-xs font-bold text-white tracking-wider uppercase">
              Details
            </h3>
          </div>

          <div className="space-y-3">
            <DetailRow label="Company Name" value={customer.name} />

            {contactName && (
              <>
                <div className="border-t border-fieldiq-border" />
                <DetailRow label="Main Contact" value={contactName} icon={<Building2 size={14} className="text-fieldiq-muted" />} />
              </>
            )}

            {contactPhone && (
              <>
                <div className="border-t border-fieldiq-border" />
                <DetailRow label="Phone" value={contactPhone} icon={<Phone size={14} className="text-fieldiq-muted" />} />
              </>
            )}

            {contactEmail && (
              <>
                <div className="border-t border-fieldiq-border" />
                <DetailRow label="Email" value={contactEmail} icon={<Mail size={14} className="text-fieldiq-muted" />} />
              </>
            )}

            {accountingEmail && (
              <>
                <div className="border-t border-fieldiq-border" />
                <DetailRow label="Accounting / AR Email" value={accountingEmail} icon={<Mail size={14} className="text-fieldiq-muted" />} />
              </>
            )}

            {address && (
              <>
                <div className="border-t border-fieldiq-border" />
                <DetailRow label="Address" value={address} icon={<MapPin size={14} className="text-fieldiq-muted" />} />
              </>
            )}

            {(city || province) && (
              <>
                <div className="border-t border-fieldiq-border" />
                <DetailRow label="City / Province" value={[city, province].filter(Boolean).join(', ')} />
              </>
            )}

            {paymentTerms && (
              <>
                <div className="border-t border-fieldiq-border" />
                <DetailRow label="Payment Terms" value={paymentTerms} icon={<CreditCard size={14} className="text-fieldiq-muted" />} />
              </>
            )}

            {defaultRateSheetName && (
              <>
                <div className="border-t border-fieldiq-border" />
                <DetailRow label="Default Price Book" value={defaultRateSheetName} icon={<FileText size={14} className="text-fieldiq-muted" />} />
              </>
            )}

            {/* Shown only when it is TRUE. A "PST exempt: No" row on every
                other client is noise, and the absence of the row is already
                the answer. */}
            {pstExempt && (
              <>
                <div className="border-t border-fieldiq-border" />
                <DetailRow
                  label="PST exempt"
                  value={pstExemptNumber ? `Yes — ${pstExemptNumber}` : 'Yes'}
                  icon={<Percent size={14} className="text-fieldiq-muted" />}
                />
              </>
            )}

            {defaultBillingFrequency && (
              <>
                <div className="border-t border-fieldiq-border" />
                <DetailRow label="Billing Frequency" value={BILLING_FREQUENCY_LABELS[defaultBillingFrequency] || defaultBillingFrequency} icon={<CalendarClock size={14} className="text-fieldiq-muted" />} />
              </>
            )}

            {defaultBillingFrequency && billingAnchor != null && (
              <>
                <div className="border-t border-fieldiq-border" />
                <DetailRow label="Billing Anchor" value={billingAnchorLabel(defaultBillingFrequency, billingAnchor)} icon={<CalendarClock size={14} className="text-fieldiq-muted" />} />
              </>
            )}

            {notes && (
              <>
                <div className="border-t border-fieldiq-border" />
                <div className="flex items-start justify-between">
                  <span className="text-sm text-white flex items-center gap-1.5">
                    <StickyNote size={14} className="text-fieldiq-muted" />
                    Notes
                  </span>
                  <span className="text-sm text-white font-medium text-right max-w-[60%]">
                    {notes}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Contacts Card */}
        <ContactsCard {...contactsCardProps} />
          {contactDeleteConfirm}
          {contactMergeDialog}
      </main>


      {/* Invite to Portal Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-fieldiq-card rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Invite to Portal</h3>
              <button onClick={() => setShowInviteModal(false)} className="text-gray-400 hover:text-white"><X size={18} /></button>
            </div>
            {inviteStatus === 'sent' ? (
              <div className="text-center py-4">
                <CheckCircle size={36} className="text-green-400 mx-auto mb-2" />
                <p className="text-green-400 font-medium">Invite sent to {inviteEmail}</p>
              </div>
            ) : (
              <>
                <p className="text-sm text-fieldiq-muted mb-4">
                  Send a portal access invite to this customer. They'll receive an email to set up their password.
                </p>
                <input
                  type="email"
                  placeholder="customer@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm mb-3 focus:outline-none focus:border-fieldiq-accent"
                  autoFocus
                />
                {inviteStatus === 'error' && (
                  <p className="text-red-400 text-sm mb-3">{inviteError}</p>
                )}
                <button
                  onClick={handleInvite}
                  disabled={!inviteEmail.trim() || inviteStatus === 'sending'}
                  className="w-full bg-fieldiq-accent text-black font-bold py-2.5 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {inviteStatus === 'sending' ? <Loader size={16} className="animate-spin" /> : <UserPlus size={16} />}
                  {inviteStatus === 'sending' ? 'Sending...' : 'Send Invite'}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-fieldiq-card rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-lg font-bold text-white mb-2">Delete Client</h3>
            <p className="text-sm text-white mb-6">
              Are you sure you want to delete &ldquo;{customer.name}&rdquo;? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-2.5 bg-fieldiq-dark text-white font-medium rounded-lg text-sm hover:text-white transition-colors"
                disabled={deleteMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="flex-1 py-2.5 bg-red-500 text-white font-semibold rounded-lg text-sm hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleteMutation.isPending && (
                  <Loader size={14} className="animate-spin" />
                )}
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Merge escape hatch (shown when delete returns 409) */}
      <MergeCustomerDialog
        open={mergeDialog.open}
        onClose={() => setMergeDialog((s) => ({ ...s, open: false }))}
        deadId={customerId!}
        deadName={customer.name}
        liveReferenceCount={mergeDialog.liveReferenceCount}
        countsPerTable={mergeDialog.countsPerTable}
        onMergeSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['customers'] });
          navigate('/customers');
        }}
      />
    </div>
  );
};

// ─── Contact Form (shared between add and edit) ─────────────────
function ContactForm({
  idPrefix,
  contact,
  setContact,
  onSave,
  onCancel,
  saving,
  saveLabel,
  customerName,
  rigSuggestions,
}: {
  idPrefix: string;
  contact: ContactDraft;
  setContact: (c: ContactDraft) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  saveLabel: string;
  customerName: string;
  rigSuggestions: string[];
}) {
  const id = (field: string) => `${idPrefix}-${field}`;
  // `role` is free text in the database. A stored value the offered list does
  // not hold gets an option of its own, or the select would show "Select
  // role..." while still sending the stored value.
  const roleOptions = contact.role && !CONTACT_ROLE_OPTIONS.some((o) => o.value === contact.role)
    ? [...CONTACT_ROLE_OPTIONS, { value: contact.role, label: contact.role }]
    : CONTACT_ROLE_OPTIONS;
  return (
    <div className="bg-fieldiq-dark/50 rounded-lg p-3 space-y-3 border border-fieldiq-border">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor={id('name')} className="text-xs text-white block mb-1">Name *</label>
          <input
            id={id('name')}
            type="text"
            value={contact.name}
            onChange={(e) => setContact({ ...contact, name: e.target.value })}
            placeholder="Contact name"
            className={inputClass}
            autoFocus
          />
        </div>
        <div>
          <label htmlFor={id('phone')} className="text-xs text-white block mb-1">Phone</label>
          <input
            id={id('phone')}
            type="tel"
            value={contact.phone}
            onChange={(e) => setContact({ ...contact, phone: e.target.value })}
            placeholder="(780) 555-0100"
            className={inputClass}
          />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor={id('email')} className="text-xs text-white block mb-1">Email</label>
          <input
            id={id('email')}
            type="email"
            value={contact.email}
            onChange={(e) => setContact({ ...contact, email: e.target.value })}
            placeholder="contact@company.com"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor={id('role')} className="text-xs text-white block mb-1">Role</label>
          <select
            id={id('role')}
            value={contact.role}
            onChange={(e) => setContact({ ...contact, role: e.target.value })}
            className={inputClass}
          >
            {roleOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label htmlFor={id('rig')} className="text-xs text-white block mb-1">Rig</label>
        <input
          id={id('rig')}
          type="text"
          list={id('rig-options')}
          value={contact.rigName}
          onChange={(e) => setContact({ ...contact, rigName: e.target.value })}
          placeholder="All rigs"
          aria-describedby={id('rig-help')}
          className={inputClass}
        />
        <datalist id={id('rig-options')}>
          {rigSuggestions.map((rig) => <option key={rig} value={rig} />)}
        </datalist>
        <p id={id('rig-help')} className="text-xs text-fieldiq-muted mt-1">
          Leave blank if this contact covers every rig for {customerName || 'this client'}.
        </p>
      </div>
      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          onClick={onSave}
          disabled={saving || !contact.name.trim()}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-fieldiq-accent text-fieldiq-dark font-bold rounded-lg text-sm hover:bg-amber-400 transition-colors disabled:opacity-50"
        >
          {saving && <Loader size={12} className="animate-spin" />}
          {saveLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-sm text-white hover:text-fieldiq-accent transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Contacts Card ──────────────────────────────────────────────
/**
 * Grouped by rig (TNT, 2026-09-27: "Need ability to edit contacts in client &
 * rig screen"). A group header is NOT a record — no tint, no click, no status —
 * and there is no collapse: this is a card, not a register.
 *
 * The Edit, Merge and Delete controls are ALWAYS drawn. They sat behind a hover-only
 * reveal, and a tablet or phone has no hover, so on the devices TNT's office
 * actually carries the controls did not exist. They are 40px targets with the
 * contact's name in the accessible name, and are drawn only for the roles the
 * API lets write (see canWriteCustomers). Merge is drawn only when the client
 * has another contact to merge into.
 *
 * "Possible duplicate" is a hint, never an action: a contact sharing phone
 * digits or a lowercased email with another of this client's contacts. It is a
 * slate badge with dark ink, so it holds in both themes.
 */
function ContactsCard({
  contacts,
  contactsLoading,
  contactsError,
  canWrite,
  customerName,
  rigSuggestions,
  showAddContact,
  setShowAddContact,
  newContact,
  setNewContact,
  editingContactId,
  editContactState,
  setEditContactState,
  onAdd,
  addPending,
  onSaveEdit,
  editPending,
  onCancelEdit,
  onStartEdit,
  onDelete,
  onMerge,
}: {
  contacts: Contact[];
  contactsLoading: boolean;
  contactsError: unknown;
  canWrite: boolean;
  customerName: string;
  rigSuggestions: string[];
  showAddContact: boolean;
  setShowAddContact: (v: boolean) => void;
  newContact: ContactDraft;
  setNewContact: (c: ContactDraft) => void;
  editingContactId: string | null;
  editContactState: ContactDraft;
  setEditContactState: (c: ContactDraft) => void;
  onAdd: () => void;
  addPending: boolean;
  onSaveEdit: (contactId: string) => void;
  editPending: boolean;
  onCancelEdit: () => void;
  onStartEdit: (c: Contact) => void;
  onDelete: (c: Contact) => void;
  onMerge: (c: Contact) => void;
}) {
  const loaded = !contactsLoading && !contactsError;
  const groups = groupContactsByRig(contacts);
  const duplicates = possibleDuplicateIds(contacts);
  const formProps = { customerName, rigSuggestions };
  return (
    <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users size={16} className="text-fieldiq-accent" />
          <h3 className="text-xs font-bold text-white tracking-wider uppercase">
            Contacts
          </h3>
          {/* No number until the list has arrived: a loading or failed count is not 0. */}
          {loaded && <span className="text-xs text-fieldiq-muted">({contacts.length})</span>}
        </div>
        {canWrite && loaded && !showAddContact && (
          <button
            type="button"
            onClick={() => { setShowAddContact(true); setNewContact(EMPTY_CONTACT); }}
            className="flex items-center gap-1 min-h-10 px-2 text-xs text-fieldiq-accent font-medium hover:text-amber-400 transition-colors"
          >
            <Plus size={14} /> Add Contact
          </button>
        )}
      </div>

      {contactsLoading && (
        <div className="flex items-center justify-center py-6">
          <Loader size={20} className="animate-spin text-fieldiq-accent" />
        </div>
      )}

      {!contactsLoading && !!contactsError && (
        <LoadFailureNotice error={contactsError} subject="contacts" className="py-6" />
      )}

      {loaded && contacts.length === 0 && !showAddContact && (
        <p className="text-sm text-fieldiq-muted text-center py-4">No contacts yet</p>
      )}

      {loaded && (
        <div className="space-y-4">
          {groups.map((group) => {
            const label = group.rigName ?? 'All rigs';
            const headingId = `contact-group-${group.key || 'all-rigs'}`.replace(/[^a-z0-9-]/gi, '-');
            return (
              <section key={group.key} aria-labelledby={headingId} data-testid="contact-group">
                <h4
                  id={headingId}
                  className="flex items-baseline gap-2 mb-2 text-xs font-semibold text-fieldiq-text"
                >
                  <span data-testid="contact-group-name">{label}</span>
                  <span className="text-fieldiq-muted font-normal" data-testid="contact-group-count">
                    {group.contacts.length}
                  </span>
                </h4>
                <div className="space-y-2">
                  {group.contacts.map((c) => {
                    const name = c.name?.trim() || null;
                    const actionName = name ?? 'unnamed contact';
                    return editingContactId === c.id ? (
                      <ContactForm
                        key={c.id}
                        idPrefix={`contact-edit-${c.id}`}
                        contact={editContactState}
                        setContact={setEditContactState}
                        onSave={() => onSaveEdit(c.id)}
                        onCancel={onCancelEdit}
                        saving={editPending}
                        saveLabel="Save"
                        {...formProps}
                      />
                    ) : (
                      <div
                        key={c.id}
                        data-testid="contact-row"
                        className="flex items-center justify-between gap-2 bg-fieldiq-dark/30 rounded-lg px-3 py-2"
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div aria-hidden="true" className="w-8 h-8 rounded-full bg-fieldiq-accent/15 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-fieldiq-accent">
                              {name ? name.charAt(0).toUpperCase() : '—'}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-white truncate" data-testid="contact-name">
                                {name ?? '—'}
                              </span>
                              {c.role && (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-fieldiq-border text-fieldiq-text flex-shrink-0">
                                  {c.role}
                                </span>
                              )}
                              {duplicates.has(c.id) && (
                                <span
                                  data-testid="possible-duplicate"
                                  title="Shares a phone number or email with another contact of this client"
                                  className="inline-flex items-center rounded-fi-pill border border-black/20 bg-fi-signal-slate px-2 py-0.5 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink flex-shrink-0"
                                >
                                  Possible duplicate
                                </span>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-fieldiq-muted mt-0.5">
                              {c.phone && (
                                <span className="flex items-center gap-1">
                                  <Phone size={10} /> {c.phone}
                                </span>
                              )}
                              {c.email && (
                                <span className="flex items-center gap-1 min-w-0 break-all">
                                  <Mail size={10} className="flex-shrink-0" /> {c.email}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        {canWrite && (
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              type="button"
                              aria-label={`Edit ${actionName}`}
                              onClick={() => onStartEdit(c)}
                              className="inline-flex items-center justify-center w-10 h-10 rounded-lg text-fieldiq-muted hover:text-white hover:bg-fieldiq-dark/50 transition-colors"
                            >
                              <Pencil size={16} />
                            </button>
                            {contacts.length > 1 && (
                              <button
                                type="button"
                                aria-label={`Merge ${actionName}`}
                                onClick={() => onMerge(c)}
                                className="inline-flex items-center justify-center w-10 h-10 rounded-lg text-fieldiq-muted hover:text-white hover:bg-fieldiq-dark/50 transition-colors"
                              >
                                <GitMerge size={16} />
                              </button>
                            )}
                            <button
                              type="button"
                              aria-label={`Delete ${actionName}`}
                              onClick={() => onDelete(c)}
                              className="inline-flex items-center justify-center w-10 h-10 rounded-lg text-fieldiq-muted hover:text-fieldiq-danger hover:bg-fieldiq-dark/50 transition-colors"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}

          {canWrite && showAddContact && (
            <ContactForm
              idPrefix="contact-new"
              contact={newContact}
              setContact={setNewContact}
              onSave={onAdd}
              onCancel={() => { setShowAddContact(false); setNewContact(EMPTY_CONTACT); }}
              saving={addPending}
              saveLabel="Add Contact"
              {...formProps}
            />
          )}
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-white flex items-center gap-1.5">
        {icon}
        {label}
      </span>
      <span className="text-sm text-white font-medium">{value}</span>
    </div>
  );
}
